import { t } from "i18next";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { Divider } from "@mui/material";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate } from "react-router-dom";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import { CircularProgress, Skeleton } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import React, { useState } from "react";
import BookingApi from "../../API/Apis/BookingApi";
import { formatTheme } from "../../config/MuiColorThemes";
import BookingGetters from "../../store/onlineBooking/getters";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useBackgroundLoader } from "../../components/UI/BgReservedArea/BackgroundLoader";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [checkEmail, setCheckEmail] = useState<boolean>(false);
  const [errorToast, setErrorToast] = useState({
    open: false,
    message: "",
    type: "",
  });

  const [errors, setErrors] = useState<{ email?: string }>({});

  const appSettings = BookingGetters.getAppSettings();
  const reservedAreaBgImageUrl = appSettings?.config?.reservedAreaBgImage;
  const defaultImage = "/assets/images/reserve_area_car.jpg";
  const { getImage, backgroundStyle } = useBackgroundLoader({
    imageUrl: reservedAreaBgImageUrl,
    defaultImage,
  });

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };
  const validateEmail = (value: string) => {
    if (!value.trim()) return t("Email is required");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return t("Enter valid email");

    return "";
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    setErrors({});
    setLoading(true);

    BookingApi.sentVerificationcode(email)
      .then((res) => {
        setLoading(false);
        if (res.status === "success") {
          setCheckEmail(true);

          setTimeout((): void => {
            setCheckEmail(false);
            navigate("/updatePassword");
          }, 5000);
        } else {
          setErrorToast({
            open: true,
            message: t("Email is not registered"),
            type: "error",
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        console.error(err);
      });
  };

  return (
    <>
      <ThemeProvider theme={dynamicTheme}>
        {loading && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            <CircularProgress sx={{ color: "white" }} />
          </div>
        )}
        <div className="relative min-h-screen flex items-center justify-center bg-transparent">
          {/* Background Image (Mobile) */}
          {getImage && (
            <div
              style={backgroundStyle}
              className="absolute inset-0 w-full h-full object-cover lg:hidden z-0"
            />
          )}
          <div className="absolute inset-0 md:hidden z-0" />

          <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
            {/* Left Image (Desktop Only) */}
            <div className="hidden lg:block w-full h-screen overflow-hidden">
              {!getImage ? (
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation="wave"
                />
              ) : (
                <div className="w-full h-full object-cover">
                  <div
                    style={backgroundStyle}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>

            {/* Right Login Section */}
            <div className="relative z-10 flex justify-center items-center max-h-screen lg:bg-gray-100">
              <div className="bg-white shadow-lg rounded-2xl px-12 py-11 w-full max-w-[22rem] md:max-w-[25rem] mx-4 border-2">
                {checkEmail && (
                  <div className="flex justify-center mb-4">
                    <CheckIcon
                      sx={{
                        color: dynamicTheme?.palette?.primary?.main,
                        width: "100px",
                        height: "100px",
                      }}
                    />
                  </div>
                )}
                <h1
                  className="text-center text-2xl font-semibold mb-6"
                  style={{
                    color: dynamicTheme?.palette?.primary?.main,
                  }}
                >
                  {checkEmail
                    ? t("Check your email")
                    : t("Forgot your password")}
                </h1>

                <div className="text-[#000000]">
                  {checkEmail
                    ? t("Exist email message")
                    : t("Enter the email address associated with your account")}
                </div>

                <div className="text-[#000000] mt-4">
                  {checkEmail
                    ? t("Check inbox message")
                    : t("We’ll send you a password reset code")}
                </div>

                {/* Form */}
                {!checkEmail && (
                  <Container component="main" disableGutters>
                    <Grid container justifyContent="center">
                      <Box
                        component="form"
                        noValidate
                        onSubmit={handleSubmit}
                        sx={{
                          width: "100%",
                          "& .MuiTextField-root": {
                            mt: 2,
                            width: "100%",
                            backgroundColor: "white",
                          },
                        }}
                      >
                        {/* Email */}
                        <TextField
                          required
                          fullWidth
                          value={email}
                          onChange={handleEmailChange}
                          id="email"
                          label={t("Email_Address")}
                          autoComplete="off"
                          error={Boolean(errors.email)}
                          helperText={
                            errors.email && (
                              <span style={{ color: "red" }}>
                                {errors.email}
                              </span>
                            )
                          }
                          FormHelperTextProps={{
                            sx: {
                              textAlign: "left",
                              ml: 0,
                            },
                          }}
                        />

                        {/* Button */}
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          sx={{
                            mt: 3,
                            mb: 2,
                            fontWeight: "bold",
                            fontSize: "17px",
                            p: 1,
                            textTransform: "none",
                          }}
                        >
                          {t("Send OTP")}
                        </Button>

                        <Divider sx={{ bgcolor: "grey.300", mt: 2, mb: 4 }} />
                      </Box>

                      {/* Back to Sign In */}
                      <Link
                        component="button"
                        underline="none"
                        onClick={() => navigate("/signin")}
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          lineHeight: "100%",
                          letterSpacing: 0,
                        }}
                      >
                        {t("Back to Sign In")}
                      </Link>
                    </Grid>
                  </Container>
                )}
              </div>
            </div>
          </div>
        </div>
        <Snackbar
          open={errorToast.open}
          autoHideDuration={4000}
          onClose={() => setErrorToast({ ...errorToast, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          ContentProps={{
            sx: {
              backgroundColor: "#d32f2f",
              color: "white",
              fontWeight: "bold",
              fontSize: "14px",
            },
          }}
          message={errorToast.message}
        />
      </ThemeProvider>
    </>
  );
}
